/**
 * 사용·오류 기록 보기 — 개발자(그리고 AI)가 무슨 일이 있었는지 확인하는 창.
 *
 * 스탠드얼론은 내부망에 홀로 놓이므로 QBase 성능관리 화면을 쓸 수 없다. 그래서 기록을 보는 최소한의 창을
 * 제품 안에 둔다. 여기서 하는 일은 셋뿐이다 — 최근 기록 보기, 한 건의 상세(스택) 펼치기, 내보내기.
 * 집계·추세 같은 것은 QPM 연동 때 그쪽 화면에 맡긴다(docs/LOGGING.md).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { FiAlertTriangle, FiActivity, FiDownload, FiRefreshCw, FiX } from 'react-icons/fi';
import { be1Fetch, BE1_API } from '../../config/api';
import { logUsage } from '../../services/eventLog';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { formatShortDateTime } from '../../utils/formatters';
import { isEnter } from '../../utils/keyboard';
import { useTranslation } from 'react-i18next';

type Kind = 'error' | 'usage';

interface LogRow {
  ts: string;
  kind: Kind;
  feature: string;
  user?: string;
  origin?: string;
  target?: string;
  durationMs?: number;
  ok?: boolean;
  detail?: Record<string, unknown>;
  error?: { message: string; name?: string; status?: number; where?: string; stack?: string };
}

/** 같은 오류를 하나로 묶은 것 — 서버가 지문으로 묶어 준다(services/error_alerts) */
interface Group {
  fingerprint: string;
  what: string;
  count: number;
  users: number;
  firstSeen?: string;
  lastSeen?: string;
  releases?: string[];
  sampleErrorId?: string;
}

const RANGES: { labelKey: string; hours: number }[] = [
  { labelKey: 'dialog:log.range1h', hours: 1 },
  { labelKey: 'dialog:log.range24h', hours: 24 },
  { labelKey: 'dialog:log.range7d', hours: 24 * 7 },
  { labelKey: 'dialog:log.rangeAll', hours: 0 },
];

const LogViewerDialog: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [kind, setKind] = useState<Kind>('error');
  // 목록보다 **묶음을 먼저** 보는 편이 조치에 가깝다 — "무엇이 제일 많이 나는가"
  const [grouped, setGrouped] = useState(true);
  const [groups, setGroups] = useState<Group[]>([]);
  const [hours, setHours] = useState(24);
  const [text, setText] = useState('');
  const [rows, setRows] = useState<LogRow[]>([]);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEscapeKey(onClose, open);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const since = hours ? new Date(Date.now() - hours * 3600_000).toISOString() : '';
      const useGroups = grouped && kind === 'error';
      const qs = new URLSearchParams(useGroups ? { kind } : { kind, limit: '300' });
      if (since) qs.set('since', since);
      if (!useGroups && text.trim()) qs.set('text', text.trim());
      const res = await be1Fetch(`/api/logs${useGroups ? '/summary' : ''}?${qs}`, { credentials: 'include' });
      if (!res.ok) throw new Error(res.status === 401 ? t('dialog:log.loginRequired') : t('dialog:log.queryFailed', { status: res.status }));
      const body = await res.json();
      if (useGroups) {
        setGroups(body.byFingerprint || []);
        setRows([]);
      } else {
        setRows(body.items || []);
        setGroups([]);
      }
      setExpanded(null);
    } catch (e) {
      setError((e as Error).message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [kind, hours, text, grouped, t]);

  useEffect(() => {
    if (!open) return;
    logUsage('log.view', { detail: { kind } });
    void load();
    // kind/hours 가 바뀔 때도 다시 읽는다. text 는 사용자가 엔터를 눌러 load 를 부른다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, kind, hours, grouped]);

  const download = () => {
    const since = hours ? new Date(Date.now() - hours * 3600_000).toISOString() : '';
    const qs = new URLSearchParams({ kind, limit: '2000' });
    if (since) qs.set('since', since);
    logUsage('log.export', { detail: { kind, hours } });
    // 내보내기는 새 탭으로 연다 — be1Fetch 는 상대 경로만 받으므로 절대 주소를 직접 만든다
    window.open(`${BE1_API || window.location.origin}/api/logs/export?${qs}`, '_blank');
  };

  if (!open) return null;

  const tab = (k: Kind, label: string, icon: React.ReactNode) => (
    <button type="button" onClick={() => setKind(k)}
      className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded ${kind === k ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
      {icon} {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black bg-opacity-50"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-[900px] max-w-[95vw] h-[640px] max-h-[88vh] bg-white rounded-lg shadow-xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 bg-gray-50 shrink-0">
          <div className="font-semibold text-gray-900">{t('dialog:log.title')}</div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label={t('common:ui.close')}>
            {/* @ts-ignore react-icons IconType 과 React 18 타입 불일치 회피 */}
            <FiX />
          </button>
        </div>

        <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-200 shrink-0 flex-wrap">
          {/* @ts-ignore */}
          {tab('error', t('dialog:log.errors'), <FiAlertTriangle size={14} />)}
          {/* @ts-ignore */}
          {tab('usage', t('dialog:log.usage'), <FiActivity size={14} />)}
          {kind === 'error' && (
            <button type="button" onClick={() => setGrouped((v) => !v)}
              title={t('dialog:log.groupTitle')}
              className={`px-2 py-1 text-xs rounded border ${grouped ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`}>
              {t('dialog:log.group')}
            </button>
          )}
          <span className="w-px h-5 bg-gray-200 mx-1" />
          {RANGES.map((r) => (
            <button key={r.labelKey} type="button" onClick={() => setHours(r.hours)}
              className={`px-2 py-1 text-xs rounded ${hours === r.hours ? 'bg-gray-800 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
              {t(r.labelKey)}
            </button>
          ))}
          <input value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (isEnter(e)) void load(); }}
            placeholder={t('dialog:log.searchPlaceholder')}
            className="ml-auto w-52 px-2 py-1 text-xs border border-gray-300 rounded" />
          <button type="button" onClick={() => void load()} title={t('dialog:log.reload')}
            className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded">
            {/* @ts-ignore */}
            <FiRefreshCw size={14} />
          </button>
          <button type="button" onClick={download} title={t('dialog:log.exportTitle')}
            className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded">
            {/* @ts-ignore */}
            <FiDownload size={14} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-auto">
          {loading && <div className="p-4 text-sm text-gray-500">{t('dialog:log.reading')}</div>}
          {!loading && error && <div className="m-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
          {!loading && !error && grouped && kind === 'error' && groups.map((g) => (
            <div key={g.fingerprint} className="border-b border-gray-100 px-4 py-2.5 text-xs hover:bg-gray-50">
              <div className="flex items-baseline gap-2">
                <span className="text-base font-semibold text-red-700 tabular-nums">{g.count}</span>
                <span className="text-gray-500">{t('dialog:log.countUnit')}</span>
                <span className="text-gray-400">·</span>
                <span className="text-gray-700">{t('dialog:log.users', { n: g.users })}</span>
                <span className="ml-auto font-mono text-[0.6875rem] text-gray-400">{g.fingerprint}</span>
              </div>
              <div className="mt-1 text-gray-800 break-all">{g.what}</div>
              <div className="mt-0.5 text-[0.6875rem] text-gray-500">
                {fmtTs(g.firstSeen || '')} ~ {fmtTs(g.lastSeen || '')}
                {g.releases && g.releases.length > 0 && t('dialog:log.builds', { list: g.releases.join(', ') })}
                {g.sampleErrorId && t('dialog:log.sample', { id: g.sampleErrorId })}
              </div>
            </div>
          ))}
          {!loading && !error && grouped && kind === 'error' && groups.length === 0 && (
            <div className="p-6 text-sm text-gray-500 text-center">{t('dialog:log.noErrors')}</div>
          )}
          {!loading && !error && !(grouped && kind === 'error') && rows.length === 0 && (
            <div className="p-6 text-sm text-gray-500 text-center">
              {t('dialog:log.noRecords')}
              {kind === 'error' && t('dialog:log.noRecordsError')}
            </div>
          )}
          {!loading && !(grouped && kind === 'error') && rows.map((r, i) => (
            <div key={i} className="border-b border-gray-100 text-xs">
              <button type="button" onClick={() => setExpanded(expanded === i ? null : i)}
                className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-start gap-2">
                <span className="text-gray-400 font-mono shrink-0 w-[130px]">{fmtTs(r.ts)}</span>
                <span className={`shrink-0 w-[150px] font-medium ${r.kind === 'error' ? 'text-red-700' : 'text-gray-800'}`}>{r.feature}</span>
                <span className="flex-1 text-gray-700 truncate">
                  {r.error?.message || r.target || describe(r.detail)}
                </span>
                <span className="shrink-0 text-gray-400">{r.user}</span>
              </button>
              {expanded === i && (
                <pre className="mx-4 mb-2 p-2 bg-gray-50 border border-gray-200 rounded overflow-auto max-h-64 whitespace-pre-wrap text-[0.6875rem] text-gray-800">
                  {JSON.stringify(r, null, 2)}
                </pre>
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between px-4 py-2 border-t border-gray-200 bg-gray-50 shrink-0 text-xs text-gray-500">
          <span>
            {grouped && kind === 'error'
              ? t('dialog:log.groupFooter', { n: groups.length })
              : t('dialog:log.rowFooter', { n: rows.length })}
          </span>
          <button type="button" onClick={onClose} className="px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 text-sm">{t('common:ui.close')}</button>
        </div>
      </div>
    </div>
  );
};

function fmtTs(ts: string): string {
  return formatShortDateTime(ts);
}

function describe(detail?: Record<string, unknown>): string {
  if (!detail) return '';
  return Object.entries(detail).map(([k, v]) => `${k}=${String(v)}`).join(' · ').slice(0, 200);
}

export default LogViewerDialog;
